import { HiringGoal } from 'api-clients/monolith';
import _round from 'lodash/round';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { UseFormReturn } from 'react-hook-form';

import { durationInDays, today } from '@/utils/date-util';

import { BudgetType, CampaignParameters } from './types';

export interface HiringTarget {
  unitBudget: number;
  targetBudget: number;
  initialTargetBudget: number;
  projectedBudget: number;
  campaignBudget: number;
  overlapDuration: (startDate?: string, endDate?: string) => number;
  remainingDurationBudget: (
    type: BudgetType,
    duration: number,
    budget?: number,
  ) => number;
}

export function useHiringTarget({
  hookForm,
  budgetSpent,
  hiringGoal,
  durationStats,
}: {
  hookForm: UseFormReturn<CampaignParameters>;
  budgetSpent: number;
  hiringGoal?: HiringGoal;
  durationStats?: {
    expected?: number;
    actual?: number;
  };
}) {
  const initialTargetBudgetRef = useRef(0);
  const {
    watch,
    formState: { isDirty, dirtyFields },
  } = hookForm;

  const inputBudget = watch('budget') || 0;
  const startsAt = watch('starts_at');
  const endAt = watch('end_at');
  const budgetType = watch('budget_type');

  /**
   * Calculates the duration of the campaign in days.
   */
  const campaignDuration = useMemo(() => {
    if (startsAt && endAt) {
      return durationInDays(startsAt, endAt);
    }
    return 0;
  }, [startsAt, endAt]);

  /**
   * Calculates how many days remain where the campaign end date overlaps with the hiring goal's deadline.
   * If the campaign ends before today, duration is 0.
   * If the campaign starts after today, use campaign's start date otherwise use today.
   * If the campaign ends before the hiring goal, use campaign's end date.
   * Otherwise, use the hiring goal's date.
   *
   * @param startDate - The start date of the campaign.
   * @param endDate - The end date of the campaign.
   * @returns The number of days where the campaign end date overlaps with the hiring goal's deadline.
   */
  const overlapDuration = useCallback(
    (
      startDate: string | undefined = startsAt,
      endDate: string | null | undefined = endAt,
    ) => {
      if (!startDate || !endDate || !hiringGoal?.hire_by) return 0;

      if (!dirtyFields.end_at) {
        const value =
          (durationStats?.expected ?? 0) - (durationStats?.actual ?? 0);
        return Math.max(value, 0); // Ensure overlap duration is never negative
      }

      const start = new Date(startDate);
      const end = new Date(endDate);
      const goalEnd = new Date(hiringGoal.hire_by);

      if (end <= today) return 0;

      const overlapStart = start > today ? start : today;

      if (end < goalEnd) return Math.max(durationInDays(overlapStart, end), 0);

      return Math.max(durationInDays(overlapStart, goalEnd), 0);
    },
    [dirtyFields.end_at, durationStats, endAt, hiringGoal?.hire_by, startsAt],
  );

  const unitBudget = useMemo(() => {
    if (!budgetType || budgetType === 'lifetime') {
      return _round(inputBudget / campaignDuration, 2);
    }
    return inputBudget;
  }, [budgetType, campaignDuration, inputBudget]);

  /**
   * Converts a per-period budget (daily, weekly, monthly) into a total budget
   * over the given duration (default is the overlap duration).
   * For lifetime budget, returns the full amount directly.
   *
   * @param type - The type of budget (daily, weekly, monthly, lifetime).
   * @param duration - The duration in days.
   * @param budget - The budget per budget type.
   * @returns The total budget over the given duration.
   */
  const remainingDurationBudget = useCallback(
    (type: BudgetType, duration: number, budget: number = unitBudget) => {
      switch (type) {
        case 'weekly':
          return Math.round(duration * (budget / 7));
        case 'monthly':
          return Math.round(duration * (budget / 30));
        case 'daily':
        case 'lifetime':
        default:
          return Math.round(duration * budget);
      }
    },
    [unitBudget],
  );

  /**
   * Budget projected based on current form input and overlap duration with the hiring goal.
   */
  const projectedBudget = useMemo(() => {
    if (!unitBudget) return 0;

    return remainingDurationBudget(budgetType, overlapDuration());
  }, [unitBudget, remainingDurationBudget, budgetType, overlapDuration]);

  /**
   * Total target budget including what has already been spent and the projected budget
   * for the remaining overlapping duration.
   */
  const targetBudget = useMemo(() => {
    return Math.round(projectedBudget + budgetSpent) || 0;
  }, [projectedBudget, budgetSpent]);

  /**
   * Calculates total campaign budget ignoring hiring goal constraints.
   * Uses remaining full campaign duration instead of hiring goal overlap.
   */
  const campaignBudget = useMemo(() => {
    if (endAt) {
      const remainingCampaignDuration = durationInDays(today, endAt);

      return (
        remainingDurationBudget(budgetType, remainingCampaignDuration) +
        budgetSpent
      );
    }
    return 0;
  }, [endAt, remainingDurationBudget, budgetType, budgetSpent]);

  // Set the initial target budget when the form is not dirty.
  useEffect(() => {
    if (!isDirty) {
      initialTargetBudgetRef.current = targetBudget;
    }
  }, [isDirty, targetBudget]);

  return {
    unitBudget,
    targetBudget,
    projectedBudget,
    initialTargetBudget: initialTargetBudgetRef.current,
    campaignBudget,
    overlapDuration,
    remainingDurationBudget,
  };
}
